/* Web Push helpers — subscribe/unsubscribe via Service Worker + backend. */
import { api } from "./api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function permissionState() {
  if (!("Notification" in window)) return "unsupported";
  return Notification.permission; // "default" | "granted" | "denied"
}

async function getRegistration() {
  if (!("serviceWorker" in navigator)) return null;
  const reg = await navigator.serviceWorker.getRegistration("/");
  return reg || (await navigator.serviceWorker.ready);
}

export async function getExistingSubscription() {
  const reg = await getRegistration();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

export async function enablePushNotifications() {
  if (!pushSupported()) throw new Error("Push notifications are not supported in this browser.");

  // 1. Ensure SW is registered & ready
  let reg = await getRegistration();
  if (!reg) {
    reg = await navigator.serviceWorker.register("/service-worker.js", { scope: "/" });
    await navigator.serviceWorker.ready;
  }

  // 2. Permission
  let perm = Notification.permission;
  if (perm === "default") perm = await Notification.requestPermission();
  if (perm !== "granted") {
    const err = new Error("Permission denied. Enable notifications in your browser settings.");
    err.code = "permission_denied";
    throw err;
  }

  // 3. VAPID key from backend
  const { data: keyResp } = await api.get("/notifications/vapid-public-key");
  const appServerKey = urlBase64ToUint8Array(keyResp.publicKey);

  // 4. Subscribe (reuse existing if any)
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: appServerKey,
    });
  }

  // 5. Persist on backend
  const raw = sub.toJSON();
  await api.post("/notifications/subscribe", {
    endpoint: raw.endpoint,
    keys: raw.keys,
    user_agent: navigator.userAgent,
  });
  return sub;
}

export async function disablePushNotifications() {
  const sub = await getExistingSubscription();
  if (!sub) return;
  const raw = sub.toJSON();
  try {
    await api.post("/notifications/unsubscribe", { endpoint: raw.endpoint });
  } catch (err) {
    console.warn("Server-side push unsubscribe failed; continuing with local unsubscribe", err);
  }
  try {
    await sub.unsubscribe();
  } catch (err) {
    console.warn("Local push unsubscribe failed", err);
  }
}

export async function sendTestNotification() {
  await api.post("/notifications/test");
}
